import {_utils} from "../../../utils/_utils.js";

export const contactUs = () => {
    return new Promise((resolve) => {
        _utils.initSection('.contact-us', (section) => init(section));
        resolve();
    })
}

const init = (section) => {
    initCustomSelects(section);

    const formContent = section.querySelector('.contact-us__form-content');
    const form = formContent?.querySelector('form');
    const thanks = section.querySelector('.contact-us__thanks');

    if (!form) return;

    // Show the "thank you" state on successful submit and scroll the section into view.
    form.addEventListener('wpcf7mailsent', () => {
        thanks?.classList.add('show');
        formContent?.classList.add('hide');

        const header = document.querySelector('.header');
        const headerHeight = header ? header.getBoundingClientRect().height : 0;
        const offset = (section.getBoundingClientRect().top + window.scrollY) - headerHeight;

        window.scrollTo({top: offset, behavior: 'smooth'});
    });

    // Disable the submit button while the request is in flight.
    form.addEventListener('submit', () => {
        const button = form.querySelector('.wpcf7-submit');
        if (button) button.disabled = true;
    });
    document.addEventListener('wpcf7submit', () => {
        section.querySelectorAll('.wpcf7-submit[disabled]').forEach((button) => {
            button.disabled = false;
        });
    });

    // Reflect CF7 validation errors on the custom selects.
    form.addEventListener('wpcf7invalid', () => {
        section.querySelectorAll('.contact-us__form-select').forEach((wrap) => {
            const real = wrap.querySelector('.real-select');
            const custom = wrap.querySelector('.custom-select');
            custom?.classList.toggle('is-error', !!real?.classList.contains('wpcf7-not-valid'));
        });
    });
}

// Custom dropdowns that drive a hidden native <select class="real-select"> (kept for CF7).
// Keyboard-operable: Tab focuses the trigger (which opens the list), Tab then walks the
// options; Enter/Space selects, Arrows move, Esc closes. Options live in a display:none
// dropdown, so they're only reachable while it's open.
const initCustomSelects = (section) => {
    // Guards a programmatic trigger.focus() (after selecting) from re-opening via focusin.
    let suppressOpen = false;

    section.querySelectorAll('.contact-us__form-select').forEach((wrap) => {
        const custom = wrap.querySelector('.custom-select');
        const trigger = wrap.querySelector('.custom-select__trigger');
        const triggerText = trigger?.querySelector('span');
        const options = [...wrap.querySelectorAll('.custom-select__option')];
        const real = wrap.querySelector('.real-select');

        if (!custom || !trigger || !real) return;

        // The custom combobox is now the accessible control, so the native <select> is kept
        // purely as a value carrier for CF7 — take it out of the tab order and the a11y tree
        // (fixes WCAG 4.1.2 "select-name" and removes the duplicate combobox / tab stop).
        real.setAttribute('tabindex', '-1');
        real.setAttribute('aria-hidden', 'true');

        // Make the custom control keyboard-focusable + expose ARIA state. The trigger is a
        // button (its accessible name comes from its visible text), the dropdown a listbox.
        if (trigger.tagName !== 'BUTTON' && !trigger.hasAttribute('tabindex')) trigger.tabIndex = 0;
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('aria-haspopup', 'listbox');
        trigger.setAttribute('aria-expanded', 'false');
        wrap.querySelector('.custom-select__dropdown')?.setAttribute('role', 'listbox');
        options.forEach((o) => {
            o.setAttribute('role', 'option');
            if (!o.hasAttribute('tabindex')) o.tabIndex = 0; // reachable only while the list is displayed
        });

        const openSelect = () => {
            document.querySelectorAll('.custom-select.open').forEach((c) => {
                if (c !== custom) {
                    c.classList.remove('open');
                    c.querySelector('.custom-select__trigger')?.setAttribute('aria-expanded', 'false');
                }
            });
            custom.classList.add('open');
            trigger.setAttribute('aria-expanded', 'true');
        };
        const closeSelect = () => {
            custom.classList.remove('open');
            trigger.setAttribute('aria-expanded', 'false');
        };

        const setValue = (value, label) => {
            real.value = value;
            real.dispatchEvent(new Event('change', {bubbles: true}));
            if (triggerText) triggerText.textContent = label;
            options.forEach((o) => o.classList.toggle('is-selected', o.dataset.value === value));
            custom.classList.toggle('is-filled', value !== '');
            custom.classList.remove('is-error');
        };

        const chooseOption = (option) => {
            setValue(option.dataset.value || '', option.textContent.trim());
            // return focus to the trigger without the focusin re-opening the list
            suppressOpen = true;
            trigger.focus();
            suppressOpen = false;
            closeSelect();
        };

        // Mouse: click toggles (mousedown captures the pre-focus state so the click after
        // focusin doesn't immediately re-close it).
        let wasOpen = false;
        trigger.addEventListener('mousedown', () => { wasOpen = custom.classList.contains('open'); });
        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            if (wasOpen) closeSelect(); else openSelect();
        });

        // Keyboard on the trigger.
        trigger.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                openSelect();
                options[0]?.focus();
            } else if (e.key === 'Escape') {
                closeSelect();
            }
        });

        // Open when focus enters (Tab or click), close when it leaves the whole control.
        custom.addEventListener('focusin', () => { if (!suppressOpen) openSelect(); });
        custom.addEventListener('focusout', (e) => {
            if (!custom.contains(e.relatedTarget)) closeSelect();
        });

        options.forEach((option, i) => {
            option.addEventListener('click', () => chooseOption(option));
            option.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    chooseOption(option);
                } else if (e.key === 'Escape') {
                    suppressOpen = true;
                    trigger.focus();
                    suppressOpen = false;
                    closeSelect();
                } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    (options[i + 1] || options[0]).focus();
                } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    (options[i - 1] || options[options.length - 1]).focus();
                }
            });
        });

        // Refill selection after a validation reload.
        if (real.value) {
            const match = options.find((o) => o.dataset.value === real.value);
            if (match) setValue(real.value, match.textContent.trim());
        }
    });

    document.addEventListener('click', () => {
        section.querySelectorAll('.custom-select.open').forEach((c) => {
            c.classList.remove('open');
            c.querySelector('.custom-select__trigger')?.setAttribute('aria-expanded', 'false');
        });
    });
}
